import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle } from '../styles/StyledComponents';

const SoftwareContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SoftwareIntro = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  
  p {
    color: var(--medium-gray);
    line-height: 1.8;
    font-size: 1.1rem;
  }
`;

const SoftwareCategories = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const CategorySection = styled(motion.div)`
  width: 100%;
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.75rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: var(--primary-blue);
  }
`;

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const LogoItem = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const SoftwareName = styled.div`
  font-weight: 600;
  color: var(--dark-blue);
  text-align: center;
`;

const SoftwarePlatforms: React.FC = () => {
  const softwareCategories = [
    {
      title: "Productivity & Collaboration",
      software: [
        "Google Workspace", 
        "Microsoft Office 365", 
        "Notion", 
        "Trello", 
        "Monday.com",
        "Slack",
        "Zoom",
        "Microsoft Teams"
      ]
    },
    {
      title: "Design & Content Creation",
      software: [
        "Canva", 
        "Adobe Photoshop", 
        "Figma", 
        "CapCut"
      ]
    },
    {
      title: "Healthcare Management",
      software: [
        "ClickUp",
        "HubSpot",
        "Salesforce Health Cloud",
        "Zoho CRM"
      ]
    }
  ];

  return (
    <Section id="software-platforms" style={{ backgroundColor: 'var(--light-gray)' }}>
      <SoftwareContainer>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Software Platforms
        </SectionTitle>
        
        <SoftwareIntro
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p>
            My expertise extends beyond healthcare-specific applications to a diverse range of software platforms 
            that enhance productivity, collaboration, and content creation. This versatility allows me to adapt 
            quickly to new environments and implement efficient workflows across various business contexts.
          </p>
        </SoftwareIntro>
        
        <SoftwareCategories>
          {softwareCategories.map((category, categoryIndex) => (
            <CategorySection
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.2 }}
            >
              <CategoryTitle>{category.title}</CategoryTitle>
              <LogoGrid>
                {category.software.map((software, index) => (
                  <LogoItem
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 + categoryIndex * 0.1 }}
                  >
                    <SoftwareName>{software}</SoftwareName>
                  </LogoItem>
                ))}
              </LogoGrid>
            </CategorySection>
          ))}
        </SoftwareCategories>
      </SoftwareContainer>
    </Section>
  );
};

export default SoftwarePlatforms;
